import { z } from "zod";

const MonoNsSchema = z.union([
  z
    .string()
    .regex(/^\d+$/, "mono ns must be a non-negative integer string")
    .transform((value) => BigInt(value)),
  z.bigint().refine((value) => value >= 0n, "mono ns must be non-negative"),
]);

export const COMMAND_LIFECYCLE_STAGES = [
  "issue",
  "authoritativeStamp",
  "receive",
  "scheduled",
  "applied",
  "observed",
] as const;

export type CommandLifecycleStage = (typeof COMMAND_LIFECYCLE_STAGES)[number];

export const CommandLifecycleTimelineSchema = z
  .object({
    issueMonoNs: MonoNsSchema,
    authoritativeStampMonoNs: MonoNsSchema,
    receiveMonoNs: MonoNsSchema,
    scheduledMonoNs: MonoNsSchema,
    appliedMonoNs: MonoNsSchema,
    observedMonoNs: MonoNsSchema,
  })
  .superRefine((timeline, ctx) => {
    const ordered: readonly [CommandLifecycleStage, bigint][] = [
      ["issue", timeline.issueMonoNs],
      ["authoritativeStamp", timeline.authoritativeStampMonoNs],
      ["receive", timeline.receiveMonoNs],
      ["scheduled", timeline.scheduledMonoNs],
      ["applied", timeline.appliedMonoNs],
      ["observed", timeline.observedMonoNs],
    ];
    for (let index = 1; index < ordered.length; index += 1) {
      const previous = ordered[index - 1];
      const current = ordered[index];
      if (!previous || !current) {
        continue;
      }
      if (current[1] < previous[1]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [current[0]],
          message: `${current[0]} must be >= ${previous[0]}`,
        });
      }
    }
  });

export const CommandLifecycleTraceSchema = z.object({
  schemaVersion: z.literal(1),
  scenarioId: z.string().min(1),
  commandId: z.string().min(1),
  seq: z.number().int().nonnegative(),
  issuerId: z.string().min(1),
  participantId: z.string().min(1),
  op: z.string().min(1),
  timeline: CommandLifecycleTimelineSchema,
});

export const CommandLifecycleTraceBundleSchema = z.object({
  schemaVersion: z.literal(1),
  traces: z.array(CommandLifecycleTraceSchema),
});

export type CommandLifecycleTimeline = z.infer<typeof CommandLifecycleTimelineSchema>;
export type CommandLifecycleTrace = z.infer<typeof CommandLifecycleTraceSchema>;
export type CommandLifecycleTraceBundle = z.infer<typeof CommandLifecycleTraceBundleSchema>;

export interface SkewSummary {
  readonly commandCount: number;
  readonly p95SkewMs: number;
  readonly p99SkewMs: number;
  readonly maxSkewMs: number;
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function percentile(values: readonly number[], rank: number): number {
  if (values.length === 0) {
    return 0;
  }
  const sorted = [...values].sort((left, right) => left - right);
  const boundedRank = Math.max(0, Math.min(1, rank));
  const index = Math.ceil(boundedRank * sorted.length) - 1;
  const safeIndex = Math.max(0, Math.min(sorted.length - 1, index));
  const value = sorted[safeIndex];
  return round3(value ?? 0);
}

function nsToMs(value: bigint): number {
  return Number(value) / 1_000_000;
}

export function summarizeAppliedSkew(
  traces: readonly CommandLifecycleTrace[],
  minParticipants = 2,
): SkewSummary {
  const byCommand = new Map<string, bigint[]>();
  for (const trace of traces) {
    const existing = byCommand.get(trace.commandId) ?? [];
    existing.push(trace.timeline.appliedMonoNs);
    byCommand.set(trace.commandId, existing);
  }

  const spreadsMs: number[] = [];
  for (const appliedNs of byCommand.values()) {
    if (appliedNs.length < minParticipants) {
      continue;
    }
    let min = appliedNs[0];
    let max = appliedNs[0];
    for (const value of appliedNs) {
      if (min === undefined || value < min) {
        min = value;
      }
      if (max === undefined || value > max) {
        max = value;
      }
    }
    if (min === undefined || max === undefined) {
      continue;
    }
    spreadsMs.push(nsToMs(max - min));
  }

  return {
    commandCount: spreadsMs.length,
    p95SkewMs: percentile(spreadsMs, 0.95),
    p99SkewMs: percentile(spreadsMs, 0.99),
    maxSkewMs: percentile(spreadsMs, 1),
  };
}

export function serializeTraceBundle(bundle: CommandLifecycleTraceBundle): string {
  return JSON.stringify(bundle, (_key, value: unknown) =>
    typeof value === "bigint" ? value.toString() : value,
  );
}

export function parseTraceBundle(input: unknown): CommandLifecycleTraceBundle {
  return CommandLifecycleTraceBundleSchema.parse(input);
}
